import { useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { http, errorMessage } from '../lib/api';
import { Spinner } from '../components/Spinner';
import { RotateCw } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export function ResetPasswordPage() {
  useDocumentTitle('Set new password');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <div className="w-full max-w-sm animate-fade-in card p-6 text-center space-y-4">
          <p className="text-sm text-ink">
            This reset link is invalid or has expired.
          </p>
          <Link to="/forgot-password" className="btn-primary inline-flex w-full justify-center text-sm">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await http.post('/auth/reset-password', { token, newPassword });
      navigate('/login', { state: { passwordReset: true }, replace: true });
    } catch (err) {
      setError(errorMessage(err, 'Unable to reset password. The link may have expired.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-4">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex flex-col items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-ink text-white">
            <RotateCw className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="text-center">
            <h1 className="text-lg font-semibold tracking-tight text-ink">
              aiai·loop
            </h1>
            <p className="mt-1 text-sm text-ink-muted">Set a new password</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="card space-y-4 p-6">
          <div>
            <label className="label" htmlFor="newPassword">
              New password
            </label>
            <input
              id="newPassword"
              name="new-password"
              type="password"
              autoComplete="new-password"
              className="input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="••••••••"
              minLength={8}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="confirmPassword">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              name="confirm-password"
              type="password"
              autoComplete="new-password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              minLength={8}
              required
            />
          </div>

          {error && (
            <div className="rounded-[5px] border border-bad/30 bg-bad-soft px-3 py-2 text-sm text-bad">
              {error}
            </div>
          )}

          <button type="submit" className="btn-primary w-full" disabled={submitting}>
            {submitting && <Spinner />}
            {submitting ? 'Saving…' : 'Set new password'}
          </button>
        </form>
      </div>
    </div>
  );
}
